import { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, ArrowLeft, ArrowRight, Plus, Warehouse, MapPin, Trash2, Pencil, ChevronDown, X } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import type { CallOff, CallOffLine, WizardDelivery, WizardDeliveryPart } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Progress } from '../../ui/progress';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import WarehouseDeliveryModal, { type WizardWarehouse } from './WarehouseDeliveryModal';
import DirectDeliveryModal from './DirectDeliveryModal';
import { useCallOffConfig } from '../../../hooks/useCallOffConfig';

interface Supplier {
  id: number;
  name: string;
  supplier_code: string;
}

interface SupplierStat {
  supplier__id: number;
  supplier__name: string;
  total: number;
  active: number;
}

interface CallOffWizardProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** When set, wizard opens in edit mode with existing call-off data */
  editCallOff?: CallOff | null;
  /** When set, the supplier picker is pre-filled with this supplier and the
   *  wizard advances straight to Step 2 — used by Stock Planning's "New
   *  Call Off" button so the user doesn't pick the supplier twice. */
  initialSupplierId?: number | null;
  /** CR-6293: seed the Open PO Days window from the launching context (e.g. the
   *  Stock Planning grid's Open PO Days), so the wizard inherits it instead of
   *  resetting to the default. */
  initialOpenPoDays?: number | null;
}

type WizardStep = 1 | 2 | 3;

function computeFillPercent(deliveries: WizardDelivery[]): number {
  let totalFill = 0;
  for (const delivery of deliveries) {
    for (const part of delivery.parts) {
      if (part.loadQty > 0) {
        totalFill += part.qty / part.loadQty;
      }
    }
  }
  return Math.round(totalFill * 100);
}

function computeTotalQty(deliveries: WizardDelivery[]): number {
  let total = 0;
  for (const delivery of deliveries) {
    for (const part of delivery.parts) {
      total += part.qty;
    }
  }
  return total;
}

/**
 * Group flat call-off lines back into wizard delivery objects,
 * grouped by delivery_type + delivery_destination + delivery_address +
 * sales_order — the same identity the backend matches lines on. Leaving
 * sales_order out merged two drops to one address that belonged to different
 * sales orders, and re-saving stamped the first order's number over both.
 *
 * Destinations are preserved as-is from the backend. Stale destinations
 * (warehouse FKs that no longer resolve to an active warehouse) are detected
 * separately by the wizard against the fetched warehouse list and surfaced
 * to the user — never silently rewritten here.
 */
function groupLinesToDeliveries(lines: CallOffLine[]): WizardDelivery[] {
  const map = new Map<string, WizardDelivery>();

  for (const line of lines) {
    const key = `${line.delivery_type}|${line.delivery_destination ?? ''}|${line.delivery_address}|${line.sales_order ?? ''}`;
    if (!map.has(key)) {
      // For direct deliveries, the address was stored as "address, postcode"
      // Split it back into separate fields for the wizard
      let address = line.delivery_address;
      let postcode = '';
      if (line.delivery_type === 'direct' && address) {
        const lastComma = address.lastIndexOf(', ');
        if (lastComma !== -1) {
          postcode = address.slice(lastComma + 2);
          address = address.slice(0, lastComma);
        }
      }

      map.set(key, {
        type: line.delivery_type,
        destinationId: line.delivery_destination,
        destinationName: line.delivery_destination_name || address || 'Delivery',
        // Older direct lines carry no stored name, so the address stands in for
        // display only — saving it back would put a street address in the
        // ship-to name (CR-7144).
        destinationNameIsFallback: !line.delivery_destination_name,
        salesOrderId: line.sales_order || null,
        address,
        postcode,
        deliveryDate: '',
        parts: [],
      });
    }
    const delivery = map.get(key)!;
    delivery.parts.push({
      partId: line.part_id ?? null,
      partNumber: line.part_number,
      description: line.description,
      qty: parseFloat(line.requested_qty) || 0,
      palletQty: parseFloat(line.pallet_qty) || 0,
      loadQty: parseFloat(line.load_qty) || 0,
    });
  }

  return Array.from(map.values());
}

function StepIndicator({ currentStep }: { currentStep: WizardStep }) {
  const steps = [
    { num: 1 as const, label: 'Supplier' },
    { num: 2 as const, label: 'Deliveries' },
    { num: 3 as const, label: 'Destination' },
  ];
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {steps.map((step, idx) => (
        <div key={step.num} className="flex items-center gap-2">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
            step.num < currentStep
              ? 'bg-green-100 text-green-700'
              : step.num === currentStep
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
          }`}>
            {step.num < currentStep ? '✓' : step.num}
            <span>{step.label}</span>
          </div>
          {idx < steps.length - 1 && (
            <div className={`w-12 h-0.5 ${
              step.num < currentStep ? 'bg-green-300' : 'bg-muted'
            }`} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function CallOffWizard({ open, onClose, onCreated, editCallOff, initialSupplierId, initialOpenPoDays }: CallOffWizardProps) {
  const isEditMode = !!editCallOff;
  const [step, setStep] = useState<WizardStep>(1);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [supplierStats, setSupplierStats] = useState<SupplierStat[]>([]);
  const [pickupDate, setPickupDate] = useState('');
  // CR-6293: open-PO availability window for this call-off. Defaults to the
  // stock-planning value (7); the user can change it in the warehouse step,
  // and the pickup date extends it (latest-wins). Persisted on the call-off.
  const [openPoDays, setOpenPoDays] = useState(7);
  const [notes, setNotes] = useState('');
  const [deliveries, setDeliveries] = useState<WizardDelivery[]>([]);
  const [saving, setSaving] = useState(false);

  // Searchable supplier dropdown state
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);

  // Active warehouses for the current customer — fetched once on open and
  // shared with the WarehouseDeliveryModal so both sides agree on what's valid.
  const [warehouses, setWarehouses] = useState<WizardWarehouse[]>([]);
  const validWarehouseIds = useMemo(
    () => new Set(warehouses.map(w => w.id)),
    [warehouses]
  );

  /**
   * A delivery is stale if it's a warehouse delivery whose destinationId
   * doesn't match any active warehouse — usually because the warehouse was
   * soft-deleted or renamed since the call-off was created.
   */
  const isStaleDelivery = useCallback(
    (delivery: WizardDelivery): boolean => {
      if (delivery.type !== 'warehouse') return false;
      // Wait until warehouses have loaded before flagging anything
      if (warehouses.length === 0) return false;
      return delivery.destinationId == null || !validWarehouseIds.has(delivery.destinationId);
    },
    [warehouses.length, validWarehouseIds]
  );

  const hasStaleDeliveries = useMemo(
    () => deliveries.some(isStaleDelivery),
    [deliveries, isStaleDelivery]
  );

  const filteredSuppliers = useMemo(() => {
    const term = supplierSearch.toLowerCase();
    if (!term) return suppliers;
    return suppliers.filter(s => s.name.toLowerCase().includes(term));
  }, [suppliers, supplierSearch]);

  // Modal states
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [showDirectModal, setShowDirectModal] = useState(false);
  const [editingDeliveryIndex, setEditingDeliveryIndex] = useState<number | null>(null);

  // Per-customer config drives whether Direct Delivery is offered.
  const { config: callOffConfig } = useCallOffConfig();

  const fillPercent = useMemo(() => computeFillPercent(deliveries), [deliveries]);
  const totalQty = useMemo(() => computeTotalQty(deliveries), [deliveries]);

  // Fill % excluding the delivery currently being edited, so the modal
  // doesn't double-count it (existing + modal = correct total)
  const fillPercentExcludingEdited = useMemo(() => {
    if (editingDeliveryIndex === null) return fillPercent;
    const otherDeliveries = deliveries.filter((_, i) => i !== editingDeliveryIndex);
    return computeFillPercent(otherDeliveries);
  }, [deliveries, editingDeliveryIndex, fillPercent]);

  const generatedReference = useMemo(() => {
    if (isEditMode && editCallOff) return editCallOff.reference;
    if (!selectedSupplier) return '';
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yy = String(now.getFullYear()).slice(-2);
    return `${selectedSupplier.supplier_code} ${mm}${yy}XX`;
  }, [selectedSupplier, isEditMode, editCallOff]);

  const fetchSuppliers = useCallback(async () => {
    setLoadingSuppliers(true);
    try {
      const res = await callOffAPI.getSuppliers();
      setSuppliers(res.data || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load suppliers';
      toast.error(message);
    } finally {
      setLoadingSuppliers(false);
    }
  }, []);

  const fetchSupplierStats = useCallback(async () => {
    try {
      const res = await callOffAPI.getSupplierStats();
      setSupplierStats(res.data || []);
    } catch {
      // Non-critical, ignore
    }
  }, []);

  const fetchWarehouses = useCallback(async () => {
    try {
      const res = await callOffAPI.getWarehouses();
      setWarehouses(res.data || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load warehouses';
      toast.error(message);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchSuppliers();
      fetchSupplierStats();
      fetchWarehouses();
    }
  }, [open, fetchSuppliers, fetchSupplierStats, fetchWarehouses]);

  // Populate wizard in edit mode
  useEffect(() => {
    if (!open || !editCallOff || suppliers.length === 0) return;

    const supplier = suppliers.find(s => s.id === editCallOff.supplier);
    if (supplier) {
      setSelectedSupplier(supplier);
    }

    setPickupDate(editCallOff.pickup_date || '');
    setOpenPoDays(editCallOff.open_po_days ?? 7);
    setNotes(editCallOff.notes || '');

    // Group lines by delivery destination into wizard deliveries
    if (editCallOff.lines && editCallOff.lines.length > 0) {
      const grouped = groupLinesToDeliveries(editCallOff.lines);
      setDeliveries(grouped);
    }

    // Skip to step 2 in edit mode
    setStep(2);
  }, [open, editCallOff, suppliers]);

  // Pre-select the supplier when the caller seeded one (e.g. Stock Planning's
  // "New Call Off" passing through the page-level supplier filter). Skipped
  // in edit mode because that flow has its own pre-fill above.
  useEffect(() => {
    if (!open || editCallOff || !initialSupplierId || suppliers.length === 0) return;
    if (selectedSupplier && selectedSupplier.id === initialSupplierId) return;

    const supplier = suppliers.find(s => s.id === initialSupplierId);
    if (supplier) {
      setSelectedSupplier(supplier);
      setStep(2);
    }
  }, [open, editCallOff, initialSupplierId, suppliers, selectedSupplier]);

  // CR-6293: inherit the Open PO Days the user set in Stock Planning when they
  // launch "New Call Off" from there (create mode only — edit mode pre-fills
  // from the saved call-off above). Depends only on open/initialOpenPoDays so it
  // won't clobber a value the user then changes inside the wizard.
  useEffect(() => {
    if (!open || editCallOff) return;
    setOpenPoDays(initialOpenPoDays ?? 7);
  }, [open, editCallOff, initialOpenPoDays]);

  const resetWizard = useCallback(() => {
    setStep(1);
    setSelectedSupplier(null);
    setSupplierSearch('');
    setSupplierDropdownOpen(false);
    setPickupDate('');
    setNotes('');
    setDeliveries([]);
    setEditingDeliveryIndex(null);
  }, []);

  const handleClose = () => {
    if (deliveries.length > 0) {
      if (!window.confirm('You have unsaved deliveries. Are you sure you want to close?')) {
        return;
      }
    }
    resetWizard();
    onClose();
  };

  const handleSupplierChange = (supplierId: string) => {
    const supplier = suppliers.find(s => String(s.id) === supplierId);
    setSelectedSupplier(supplier || null);
  };

  const selectedSupplierStat = useMemo(() => {
    if (!selectedSupplier) return null;
    return supplierStats.find(s => s['supplier__id'] === selectedSupplier.id) || null;
  }, [selectedSupplier, supplierStats]);

  const handleAddWarehouseDelivery = (delivery: WizardDelivery) => {
    if (editingDeliveryIndex !== null) {
      setDeliveries(prev => {
        const updated = [...prev];
        updated[editingDeliveryIndex] = delivery;
        return updated;
      });
      setEditingDeliveryIndex(null);
    } else {
      setDeliveries(prev => [...prev, delivery]);
    }
    setShowWarehouseModal(false);
    setStep(2);
  };

  const handleAddDirectDeliveries = (newDeliveries: WizardDelivery[]) => {
    if (editingDeliveryIndex !== null) {
      // Replace the edited delivery
      setDeliveries(prev => {
        const updated = [...prev];
        updated.splice(editingDeliveryIndex, 1, ...newDeliveries);
        return updated;
      });
      setEditingDeliveryIndex(null);
    } else {
      setDeliveries(prev => [...prev, ...newDeliveries]);
    }
    setShowDirectModal(false);
    setStep(2);
  };

  const handleRemoveDelivery = (index: number) => {
    setDeliveries(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditDelivery = (index: number) => {
    const delivery = deliveries[index];
    setEditingDeliveryIndex(index);
    if (delivery.type === 'warehouse') {
      setShowWarehouseModal(true);
    } else {
      setShowDirectModal(true);
    }
  };

  const handleBackFromStep2 = () => {
    if (deliveries.length > 0) {
      if (!window.confirm('Going back will clear your deliveries. Continue?')) {
        return;
      }
      setDeliveries([]);
    }
    setStep(1);
  };

  const handleCreate = async () => {
    if (!selectedSupplier || !pickupDate || deliveries.length === 0) return;

    setSaving(true);
    try {
      // Flatten deliveries into lines
      const lines = deliveries.flatMap(delivery =>
        delivery.parts.map((part: WizardDeliveryPart) => ({
          part_id: part.partId ?? undefined,
          part_number: part.partNumber,
          description: part.description,
          requested_qty: part.qty,
          pallet_qty: part.palletQty,
          load_qty: part.loadQty,
          delivery_type: delivery.type,
          delivery_address: delivery.type === 'direct'
            ? `${delivery.address}, ${delivery.postcode}`
            : '',
          sales_order: delivery.salesOrderId || '',
          delivery_destination_id: delivery.destinationId ?? undefined,
          // A direct drop has no warehouse to name it, so the name travels with
          // the line. Without it, reopening the call-off could only label the
          // drop with its street address — which then became the call-off's
          // destination_name and overflowed the column (CR-7144).
          delivery_destination_name:
            delivery.type === 'direct' && !delivery.destinationNameIsFallback
              ? delivery.destinationName
              : '',
        }))
      );

      // Determine destination_type
      const types = new Set(deliveries.map(d => d.type));
      let destinationType: 'warehouse' | 'direct' | 'mixed' = 'warehouse';
      if (types.has('warehouse') && types.has('direct')) {
        destinationType = 'mixed';
      } else if (types.has('direct')) {
        destinationType = 'direct';
      }

      // Check fill % > 100% and confirm
      if (fillPercent > 100) {
        const confirmed = window.confirm(
          `Container fill is ${fillPercent}% (over 100%). Are you sure you want to proceed?`
        );
        if (!confirmed) {
          setSaving(false);
          return;
        }
      }

      // Build destination_name — summarise all destinations for mixed deliveries
      let destinationName = deliveries[0]?.destinationName || '';
      if (destinationType === 'mixed') {
        const warehouseNames = deliveries
          .filter(d => d.type === 'warehouse')
          .map(d => d.destinationName)
          .filter(Boolean);
        const directNames = deliveries
          .filter(d => d.type === 'direct')
          .map(d => d.destinationName)
          .filter(Boolean);
        const unique = Array.from(new Set([...warehouseNames, ...directNames]));
        destinationName = `Mixed: ${unique.join(', ')}`;
      }

      if (isEditMode && editCallOff) {
        // Edit mode: PATCH existing call-off
        await callOffAPI.updateCallOff(editCallOff.id, {
          pickup_date: pickupDate,
          open_po_days: openPoDays,
          destination_type: destinationType,
          destination_name: destinationName,
          notes,
          lines,
        });
        toast.success('Call-off updated successfully');
      } else {
        // Create mode
        await callOffAPI.createCallOff({
          supplier_id: selectedSupplier.id,
          pickup_date: pickupDate,
          open_po_days: openPoDays,
          destination_type: destinationType,
          destination_name: destinationName,
          notes,
          lines,
        });
        toast.success('Call-off created successfully');
      }

      resetWizard();
      onCreated();
      onClose();
    } catch (err: unknown) {
      const fallback = isEditMode ? 'Failed to update call-off' : 'Failed to create call-off';
      toast.error(getApiErrorMessage(err, fallback));
    } finally {
      setSaving(false);
    }
  };

  const editDelivery = editingDeliveryIndex !== null ? deliveries[editingDeliveryIndex] : undefined;

  return (
    <>
      <Dialog open={open && !showWarehouseModal && !showDirectModal} onOpenChange={(v) => { if (!v) handleClose(); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditMode ? `Call-Off: ${editCallOff?.reference}` : 'New Call Off'}</DialogTitle>
            <DialogDescription>
              {isEditMode ? 'Edit deliveries, quantities, and pickup details.' : 'Create a new call-off with one or more deliveries.'}
            </DialogDescription>
          </DialogHeader>

          <StepIndicator currentStep={step} />

          {/* Step 1: Select Supplier (hidden in edit mode) */}
          {step === 1 && !isEditMode && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Supplier</Label>
                <div className="relative">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={loadingSuppliers ? 'Loading...' : 'Search suppliers...'}
                      value={selectedSupplier ? selectedSupplier.name : supplierSearch}
                      onChange={(e) => {
                        setSupplierSearch(e.target.value);
                        setSelectedSupplier(null);
                        setSupplierDropdownOpen(true);
                      }}
                      onFocus={() => setSupplierDropdownOpen(true)}
                      className="w-full px-3 py-2 pr-8 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                    {selectedSupplier && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedSupplier(null);
                          setSupplierSearch('');
                        }}
                        className="absolute right-8 top-2 p-1 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {supplierDropdownOpen && filteredSuppliers.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                      {filteredSuppliers.slice(0, 50).map(s => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => {
                            handleSupplierChange(String(s.id));
                            setSupplierSearch('');
                            setSupplierDropdownOpen(false);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-muted text-sm"
                        >
                          {s.name}
                        </button>
                      ))}
                    </div>
                  )}
                  {supplierDropdownOpen && (
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setSupplierDropdownOpen(false)}
                    />
                  )}
                </div>
              </div>

              {selectedSupplier && (
                <Card>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Supplier Code</span>
                      <span className="font-mono text-sm">{selectedSupplier.supplier_code}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Reference Preview</span>
                      <span className="font-mono text-sm text-muted-foreground">{generatedReference}</span>
                    </div>
                    {selectedSupplierStat && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Total Call-Offs</span>
                          <Badge variant="secondary">{selectedSupplierStat.total}</Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Active</span>
                          <Badge variant="secondary">{selectedSupplierStat.active}</Badge>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Step 2: Deliveries Hub */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Header info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Reference</Label>
                  <p className="font-mono text-sm">{generatedReference}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Supplier</Label>
                  <p className="text-sm">{selectedSupplier?.name}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pickup Date <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    value={pickupDate}
                    onChange={(e) => setPickupDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional notes"
                    rows={2}
                  />
                </div>
              </div>

              {/* Container fill bar */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">Container Fill</span>
                  <span className={`font-bold ${fillPercent > 90 ? 'text-red-600' : fillPercent > 50 ? 'text-orange-600' : 'text-green-600'}`}>
                    {fillPercent}%
                  </span>
                </div>
                <Progress value={Math.min(fillPercent, 100)} className="h-3" />
                <p className="text-xs text-muted-foreground">
                  {totalQty} total units across {deliveries.length} {deliveries.length === 1 ? 'delivery' : 'deliveries'}
                </p>
              </div>

              {/* Delivery cards */}
              {deliveries.length > 0 && (
                <div className="space-y-2">
                  <Label>Deliveries</Label>
                  {hasStaleDeliveries && (
                    <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
                      <span className="font-semibold">⚠ Action required:</span>
                      <span>
                        One or more deliveries point to a warehouse that no longer exists.
                        Edit each flagged delivery and pick a current warehouse before saving.
                      </span>
                    </div>
                  )}
                  {deliveries.map((delivery, idx) => {
                    const stale = isStaleDelivery(delivery);
                    return (
                      <Card
                        key={idx}
                        className={`border-l-4 ${
                          stale
                            ? 'border-l-red-600 bg-red-50/50 ring-1 ring-red-300'
                            : delivery.type === 'warehouse'
                              ? 'border-l-red-500'
                              : 'border-l-blue-500'
                        }`}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {delivery.type === 'warehouse' ? (
                                <Warehouse className={`h-4 w-4 ${stale ? 'text-red-600' : 'text-red-500'}`} />
                              ) : (
                                <MapPin className="h-4 w-4 text-blue-500" />
                              )}
                              <div>
                                <p className={`text-sm font-medium ${stale ? 'text-red-700' : ''}`}>
                                  {stale
                                    ? `⚠ Unknown destination${delivery.destinationName ? ` (was: ${delivery.destinationName})` : ''} — please re-select`
                                    : delivery.destinationName}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {delivery.type === 'warehouse' ? 'Warehouse' : 'Direct'} &middot;{' '}
                                  {delivery.parts.length} {delivery.parts.length === 1 ? 'part' : 'parts'} &middot;{' '}
                                  {delivery.parts.reduce((sum, p) => sum + p.qty, 0)} units
                                </p>
                                {delivery.type === 'direct' && delivery.address && (
                                  <p className="text-xs text-muted-foreground">{delivery.address}, {delivery.postcode}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleEditDelivery(idx)}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleRemoveDelivery(idx)}>
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Add delivery button */}
              <Button variant="outline" className="w-full gap-2" onClick={() => setStep(3)}>
                <Plus className="h-4 w-4" />
                Add Delivery
              </Button>
            </div>
          )}

          {/* Step 3: Choose Delivery Type */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Choose the type of delivery to add:</p>
              <div className={`grid gap-4 ${callOffConfig.direct_delivery_enabled ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <Card
                  className="cursor-pointer hover:border-red-300 hover:bg-red-50/50 transition-colors"
                  onClick={() => {
                    setEditingDeliveryIndex(null);
                    setShowWarehouseModal(true);
                  }}
                >
                  <CardContent className="p-6 text-center space-y-3">
                    <Warehouse className="h-12 w-12 mx-auto text-red-500" />
                    <div>
                      <p className="font-medium text-lg">Warehouse</p>
                      <p className="text-sm text-muted-foreground">
                        Select parts from stock planning grid
                      </p>
                    </div>
                  </CardContent>
                </Card>
                {callOffConfig.direct_delivery_enabled && (
                  <Card
                    className="cursor-pointer hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
                    onClick={() => {
                      setEditingDeliveryIndex(null);
                      setShowDirectModal(true);
                    }}
                  >
                    <CardContent className="p-6 text-center space-y-3">
                      <MapPin className="h-12 w-12 mx-auto text-blue-500" />
                      <div>
                        <p className="font-medium text-lg">Direct Delivery</p>
                        <p className="text-sm text-muted-foreground">
                          Select from sales orders for direct delivery
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            {step === 1 && (
              <>
                <Button variant="outline" onClick={handleClose}>Cancel</Button>
                <Button
                  onClick={() => setStep(2)}
                  disabled={!selectedSupplier}
                  className="gap-2"
                >
                  Next
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </>
            )}
            {step === 2 && (
              <>
                {!isEditMode && (
                  <Button variant="outline" onClick={handleBackFromStep2} className="gap-2">
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Button>
                )}
                {isEditMode && (
                  <Button variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={handleCreate}
                  disabled={saving || !pickupDate || deliveries.length === 0 || hasStaleDeliveries}
                  title={hasStaleDeliveries ? 'Fix flagged deliveries before saving' : undefined}
                  className="gap-2"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isEditMode ? 'Save Changes' : 'Create Call-Off'}
                </Button>
              </>
            )}
            {step === 3 && (
              <Button variant="outline" onClick={() => setStep(2)} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warehouse delivery modal */}
      {selectedSupplier && (
        <WarehouseDeliveryModal
          open={showWarehouseModal}
          onClose={() => {
            setShowWarehouseModal(false);
            setEditingDeliveryIndex(null);
          }}
          onAdd={handleAddWarehouseDelivery}
          supplierId={selectedSupplier.id}
          existingFillPercent={fillPercentExcludingEdited}
          editDelivery={editDelivery?.type === 'warehouse' ? editDelivery : undefined}
          warehouses={warehouses}
          openPoDays={openPoDays}
          setOpenPoDays={setOpenPoDays}
          pickupDate={pickupDate}
        />
      )}

      {/* Direct delivery modal */}
      {selectedSupplier && (
        <DirectDeliveryModal
          open={showDirectModal}
          onClose={() => {
            setShowDirectModal(false);
            setEditingDeliveryIndex(null);
          }}
          onAdd={handleAddDirectDeliveries}
          supplierId={selectedSupplier.id}
          supplierName={selectedSupplier.name}
          existingFillPercent={fillPercentExcludingEdited}
          editDelivery={editDelivery?.type === 'direct' ? editDelivery : undefined}
        />
      )}
    </>
  );
}
